<template>
  <div class="physical-exam">
    <!-- 患者信息 -->
    <PatientInfo :patientData="patientData" />
    
    <!-- 动态字段展示（支持引用推荐） -->
    <div v-if="activeFields && activeFields.length > 0" class="fields-container">
      <div
        v-for="(field, index) in activeFields"
        :key="index"
        class="field-container"
        :data-index="index"
      >
        <div class="flex items-start gap-2">
          <!-- 字段标题 -->
          <div class="field-title flex-shrink-0">
            <strong>{{ field.label || field.title }}：</strong>
          </div>
          <!-- 字段内容区域 -->
          <div class="flex-1 min-w-0">
            <!-- 编辑模式 -->
            <div v-if="field.isEdit" class="edit-mode">
              <el-input
                v-model="field.value"
                type="textarea"
                :rows="field.key === 'description' ? 4 : 2"
                placeholder="请输入内容..."
                @blur="handleInputBlur(field, index)"
                @click.stop
                class="edit-input"
              ></el-input>
              <div class="edit-buttons">
                <button
                  @mousedown="cancelEdit(field, index)"
                  class="cancel-edit-btn"
                >取消编辑</button>
              </div>
            </div>

            <!-- 显示模式 -->
            <div v-else>
              <!-- 检查是否有修改建议 -->
              <div v-if="field.isEdit_newValue && field.isEdit_newValue != '未提供'" class="suggestion-mode">
                <!-- 原始内容 - 红框 -->
                <div class="original-content">
                  <div class="original-text-field" @click="editBtn(field, index)" @click.stop>
                    <div class="content-wrapper">
                      <div class="whitespace-pre-wrap content-text" v-if="field.value">{{ field.value }}</div>
                      <div class="placeholder-text" v-else>点击输入内容...</div>
                    </div>
                  </div>
                </div>
                <!-- 建议内容 - 绿色，点击可编辑 -->
                <div class="suggested-content">
                  <!-- 建议编辑模式 -->
                  <div v-if="field.isEditingSuggestion" class="suggestion-edit-mode">
                    <el-input
                      v-model="field.isEdit_newValue"
                      type="textarea"
                      :rows="field.key === 'description' ? 4 : 2"
                      placeholder="请编辑建议内容..."
                      @blur="finishEditSuggestion(field, index)"
                      @click.stop
                      class="edit-suggestion-input"
                    ></el-input>
                  </div>
                  <!-- 建议内容显示模式 -->
                  <span
                    v-else
                    class="suggested-text clickable-suggestion"
                    @click="editSuggestion(field, index)"
                    @click.stop
                    title="点击编辑建议内容"
                  >
                    {{ field.isEdit_newValue }}
                  </span>
                  <!-- 操作按钮 -->
                  <div class="suggestion-buttons">
                    <button class="accept-btn" @click="handleAccept(field, index)">
                      <i class="el-icon-check"></i>接受
                    </button>
                    <button class="reject-btn" @click="handleRefuse(field, index)">
                      <i class="el-icon-close"></i>拒绝
                    </button>
                  </div>
                </div>
              </div>

              <!-- 正常显示模式 -->
              <div v-else class="editable-field-content" @click="editBtn(field, index)" @click.stop>
                <div class="content-wrapper">
                  <div class="whitespace-pre-wrap content-text" v-if="field.value">{{ field.value }}</div>
                  <div class="placeholder-text" v-else>点击输入内容...</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 固定字段（兼容旧数据） -->
    <template v-else>
      <div class="physical-exam-grid">
        <div class="field-item">
          <div class="field-label">体温：</div>
          <div class="field-value">
            <el-input
              v-model="formData.temperature"
              placeholder="请输入体温"
              @blur="handleBlur"
            />
          </div>
        </div>
        
        <div class="field-item">
          <div class="field-label">脉搏：</div>
          <div class="field-value">
            <el-input
              v-model="formData.pulse"
              placeholder="请输入脉搏"
              @blur="handleBlur"
            />
          </div>
        </div>
        
        <div class="field-item">
          <div class="field-label">呼吸：</div>
          <div class="field-value">
            <el-input
              v-model="formData.respiration"
              placeholder="请输入呼吸"
              @blur="handleBlur"
            />
          </div>
        </div>
        
        <div class="field-item">
          <div class="field-label">血压：</div>
          <div class="field-value">
            <el-input
              v-model="formData.bloodPressure"
              placeholder="请输入血压"
              @blur="handleBlur"
            />
          </div>
        </div>
        
        <div class="field-item">
          <div class="field-label">身高：</div>
          <div class="field-value">
            <el-input
              v-model="formData.height"
              placeholder="请输入身高"
              @blur="handleBlur"
            />
          </div>
        </div>
        
        <div class="field-item">
          <div class="field-label">体重：</div>
          <div class="field-value">
            <el-input
              v-model="formData.weight"
              placeholder="请输入体重"
              @blur="handleBlur"
            />
          </div>
        </div>
      </div>
      
      <div class="field-item">
        <div class="field-label">描述：</div>
        <div class="field-value">
          <el-input
            type="textarea"
            :rows="4"
            v-model="formData.description"
            placeholder="请输入描述"
            @blur="handleBlur"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import PatientInfo from './PatientInfo.vue';

export default {
  name: 'PhysicalExam',
  components: {
    PatientInfo
  },
  props: {
    patientData: {
      type: Object,
      default: () => ({})
    },
    value: {
      type: Object,
      default: () => ({
        temperature: '',
        pulse: '',
        respiration: '',
        bloodPressure: '',
        height: '',
        weight: '',
        description: '',
        fields: []
      })
    }
  },
  data() {
    return {
      formData: {
        temperature: '',
        pulse: '',
        respiration: '',
        bloodPressure: '',
        height: '',
        weight: '',
        description: ''
      },
      activeFields: [],
      isCancelling: false
    };
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal) {
          // 优先使用 fields（动态字段）
          if (Array.isArray(newVal.fields) && newVal.fields.length > 0) {
            this.activeFields = newVal.fields.map(f => ({
              key: f.key,
              label: f.label || f.title,
              title: f.label || f.title,
              value: f.value || '',
              originalValue: f.value || '',
              isEdit: false,
              // 优先使用父组件显式的 isEdit_newValue（含 null），避免 suggestedValue 把已接受的建议又刷回来
              isEdit_newValue: Object.prototype.hasOwnProperty.call(f, 'isEdit_newValue')
                ? f.isEdit_newValue
                : (f.suggestedValue || null),
              isEditingSuggestion: false
            }));
          } else {
            // 使用固定字段（兼容旧数据）
            this.formData = {
              temperature: newVal.temperature || '',
              pulse: newVal.pulse || '',
              respiration: newVal.respiration || '',
              bloodPressure: newVal.bloodPressure || '',
              height: newVal.height || '',
              weight: newVal.weight || '',
              description: newVal.description || ''
            };
            this.activeFields = [];
          }
        }
      }
    }
  },
  methods: {
    handleBlur() {
      this.$emit('input', { ...this.formData });
      this.$emit('change', { ...this.formData });
      // 触发父组件的保存方法
      this.$emit('save');
    },
    
    // 获取当前数据
    getData() {
      if (this.activeFields.length > 0) {
        return {
          fields: this.activeFields.map(f => ({
            key: f.key,
            label: f.label,
            value: f.value,
            suggestedValue: f.isEdit_newValue
          }))
        };
      }
      return { ...this.formData };
    },

    /* 编辑模式 */
    editBtn(field, index) {
      const currentEditingIndex = this.activeFields.findIndex(item => item.isEdit);
      if (currentEditingIndex !== -1 && currentEditingIndex !== index) {
        const currentEditingField = this.activeFields[currentEditingIndex];
        this.saveField(currentEditingField, currentEditingIndex, true, false);
      }

      if (field.originalValue === undefined) {
        field.originalValue = field.value;
      }
      this.$set(this.activeFields, index, { ...field, isEdit: true });
    },

    /* 取消编辑 */
    cancelEdit(field, index) {
      this.isCancelling = true;
      if (field.originalValue !== undefined) {
        field.value = field.originalValue;
        delete field.originalValue;
      }
      this.$set(this.activeFields, index, { ...field, isEdit: false });
      this.$nextTick(() => {
        this.isCancelling = false;
      });
    },

    /* 处理输入框失焦事件 */
    handleInputBlur(field, index) {
      if (this.isCancelling) {
        return;
      }
      const hasChanged = field.originalValue !== undefined && field.value !== field.originalValue;
      if (hasChanged) {
        setTimeout(() => {
          this.saveField(field, index, true, false);
        }, 100);
      } else {
        this.saveField(field, index, false, false);
      }
    },

    /* 保存字段 */
    saveField(field, index, shouldSave = true, isSwitch = false) {
      if (field.originalValue !== undefined) {
        delete field.originalValue;
      }
      if (!isSwitch) {
        this.$set(this.activeFields, index, { ...field, isEdit: false });
      }
      if (shouldSave) {
        // 更新数据并通知父组件
        this.$emit('input', { fields: this.activeFields.map(f => ({
          key: f.key,
          label: f.label,
          value: f.value,
          suggestedValue: f.isEdit_newValue
        })) });
        // 触发父组件的保存方法
        this.$emit('save');
      }
    },

    /* 编辑建议内容 */
    editSuggestion(field, index) {
      this.$set(this.activeFields, index, { ...field, isEditingSuggestion: true });
    },

    /* 完成建议编辑 */
    finishEditSuggestion(field, index) {
      this.$set(this.activeFields, index, { ...field, isEditingSuggestion: false });
    },

    /* 接受 */
    handleAccept(field, index) {
      const newValue = field.isEdit_newValue;
      this.$set(this.activeFields, index, {
        ...field,
        value: newValue,
        isEdit_newValue: null
      });
      this.$emit('input', { fields: this.activeFields.map(f => ({
        key: f.key,
        label: f.label,
        value: f.value,
        suggestedValue: f.isEdit_newValue,
        isEdit_newValue: f.isEdit_newValue
      })) });
      // 触发保存事件，让父组件知道数据已被修改
      this.$emit('save');
    },

    /* 拒绝 */
    handleRefuse(field, index) {
      this.$set(this.activeFields, index, { ...field, isEdit_newValue: null });
      this.$emit('input', { fields: this.activeFields.map(f => ({
        key: f.key,
        label: f.label,
        value: f.value,
        suggestedValue: f.isEdit_newValue,
        isEdit_newValue: f.isEdit_newValue
      })) });
    }
  }
};
</script>

<style lang="less" scoped>
.physical-exam {
  padding: 1rem 1.25rem;
  background: #fff;
  
  .physical-exam-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 12px;
  }
  
  .field-item {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background: #fff;
    border: none;
    border-bottom: 1px solid #e5e5ea;
    border-radius: 0;
    transition: all 0.3s ease;
    
    &:last-child {
      border-bottom: none;
      grid-column: 1 / -1;
      align-items: flex-start;
    }
    
    .field-label {
      flex-shrink: 0;
      width: 80px;
      font-weight: 600;
      color: #333;
      margin-right: 12px;
      padding-top: 4px;
    }
    
    .field-value {
      flex: 1;
      
      ::v-deep .el-input__inner,
      ::v-deep .el-textarea__inner {
        border: 1px solid #d1d1d6;
        border-radius: 8px;
        resize: vertical;
      }
    }
  }

  // 动态字段样式（引用门诊病历样式）
  .fields-container {
    .field-container {
      margin-bottom: 12px;
      padding: 12px 16px;
      background: #fff;
      border-bottom: 1px solid #e5e5ea;

      &:last-child {
        border-bottom: none;
      }

      .field-title {
        width: 80px;
        color: #333;
        font-weight: 600;
        line-height: 28px;
        padding-top: 4px;
        font-size: 14px;
      }

      .edit-mode {
        .edit-input {
          margin-bottom: 8px;
        }
        .edit-buttons {
          text-align: right;
          .cancel-edit-btn {
            padding: 4px 12px;
            background: #f5f5f5;
            border: 1px solid #ddd;
            border-radius: 4px;
            cursor: pointer;
            &:hover {
              background: #e5e5e5;
            }
          }
        }
      }

      .suggestion-mode {
        .original-content {
          margin-bottom: 0.75rem;

          .original-text-field {
            background-color: #fef2f2;
            border: 2px solid #fca5a5;
            padding: 0.5rem 0.75rem;
            border-radius: 0.375rem;
            font-size: 0.95rem;
            color: #991b1b;
            display: block;
            line-height: 1.6;
            min-height: 60px; // 设置最小高度
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
              background-color: #fef7f7;
              border-color: #f87171;
            }
          }
        }
        .suggested-content {
          position: relative;
          margin-bottom: 2rem; // 为框外按钮预留空间

          .suggested-text {
            background-color: #f0fdf4;
            color: #166534;
            border: 2px solid #86efac;
            padding: 0.5rem 0.75rem;
            border-radius: 0.375rem;
            font-size: 0.95rem;
            display: block;
            line-height: 1.6;
            min-height: 2.5rem;
            white-space: pre-wrap;
            cursor: pointer;
          }

          .suggestion-buttons {
            position: absolute;
            top: 100%; // 从框的底部开始
            margin-top: 0.25rem; // 在边框线下方留出小间距
            right: 0.5rem; // 框外右侧，与框右对齐
            display: flex;
            gap: 0.25rem;
            z-index: 10; // 确保按钮在最上层

            .accept-btn, .reject-btn {
              font-weight: 500;
              font-size: 0.75rem;
              line-height: 1rem;
              padding-top: 0.25rem;
              padding-bottom: 0.25rem;
              padding-left: 0.75rem;
              padding-right: 0.75rem;
              line-height: 26px;
              border: none;
              border-radius: 9999px !important;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.2) !important;
              transition: all 0.3s !important;
              cursor: pointer;

              &:hover {
                cursor: pointer;
                transform: translateY(-3px);
              }

              &:active {
                transform: translateY(-1px);
              }

              i {
                margin-right: 4px;
              }
            }

            .accept-btn {
              background-color: #34c759 !important;
              color: white !important;
            }

            .reject-btn {
              background-color: #d32f2f !important;
              color: white !important;
            }
          }
        }
      }

      .editable-field-content {
        padding: 8px;
        border: 1px solid #d1d1d6;
        border-radius: 4px;
        cursor: pointer;
        min-height: 40px;
        &:hover {
          border-color: #4169e1;
          background: #f8faff;
        }
        .content-text {
          white-space: pre-wrap;
        }
        .placeholder-text {
          color: #999;
        }
      }
    }
  }
}
</style>
